import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, tap } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { BusinessPartnerAddressService } from '../../business-partner/business-partner-address-modal/business-partner-address.component.service';
import { PendingAddress, PageInfo } from '../interfaces/pending-address.interface';
import { AddressFilters } from '../interfaces/address-filters.interface';
import { SyncResponse, SingleSyncResponse } from '../interfaces/sync-response.interface';
import { SYNCABLE_ADDRESS_STATUSES, SYNCABLE_ERP_STATUSES } from '../enums/erp-status.enum';

@Injectable({
  providedIn: 'root'
})
export class AddressPendingSyncStateService {
  // Estado reactivo
  private addressesSubject = new BehaviorSubject<PendingAddress[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private totalItemsSubject = new BehaviorSubject<number>(0);
  private pageInfoSubject = new BehaviorSubject<PageInfo | null>(null);

  // Streams públicos
  readonly addresses$ = this.addressesSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();
  readonly totalItems$ = this.totalItemsSubject.asObservable();
  readonly pageInfo$ = this.pageInfoSubject.asObservable();

  // Estado de filtros actual
  private currentFiltersSubject = new BehaviorSubject<AddressFilters>({});
  readonly currentFilters$ = this.currentFiltersSubject.asObservable();

  constructor(
    private businessPartnerAddressService: BusinessPartnerAddressService
  ) {}

  /**
   * Carga direcciones pendientes usando el servicio existente
   */
  loadPendingAddresses(filters: AddressFilters = {}): Observable<PendingAddress[]> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);
    this.currentFiltersSubject.next(filters);

    return this.businessPartnerAddressService.findPendingAddressesForSync(filters).pipe(
      tap(response => {
        // Guardar pageInfo si está disponible - es un array, tomar la primera posición
        if (response.data?.pageInfo && response.data.pageInfo.length > 0) {
          this.pageInfoSubject.next(response.data.pageInfo[0]);
          this.totalItemsSubject.next(response.data.pageInfo[0].total || 0);
        }
      }),
      map(response => {
        // La nueva estructura tiene data.data para el array de direcciones
        const addresses = response.data?.data || [];
        // Filtrar solo las direcciones con estados sincronizables
        return addresses.filter(address => 
          SYNCABLE_ADDRESS_STATUSES.includes(address.statusERP)
        );
      }),
      tap(addresses => {
        this.addressesSubject.next(addresses);
        // Si no hay pageInfo, usar el length del array filtrado
        if (!this.pageInfoSubject.value) {
          this.totalItemsSubject.next(addresses.length);
        }
      }),
      catchError(error => {
        const errorMessage = error.error?.message || 'Error al cargar direcciones pendientes';
        this.errorSubject.next(errorMessage);
        this.addressesSubject.next([]);
        this.totalItemsSubject.next(0);
        this.pageInfoSubject.next(null);
        return of([]);
      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Sincroniza múltiples direcciones usando el servicio existente
   */
  syncAddresses(addressIds: string[]): Observable<HttpResponse<SyncResponse>> {
    if (!addressIds.length) {
      return of(new HttpResponse({
        body: {
          status: 'error',
          statusCode: 'INVALID_INPUT',
          message: 'No se proporcionaron direcciones para sincronizar'
        },
        status: 400
      }));
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.businessPartnerAddressService.syncAddressesWithERP(addressIds).pipe(
      tap(response => {
        // Recargar direcciones después de sincronización exitosa
        this.refreshAddresses();
      }),
      catchError(error => {
        const errorMessage = error.error?.message || 'Error al sincronizar direcciones';
        this.errorSubject.next(errorMessage);
        return of(new HttpResponse({
          body: {
            status: 'error',
            statusCode: 'SYNC_ERROR',
            message: errorMessage
          },
          status: 500
        }));
      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Sincroniza una sola dirección usando el servicio existente
   */
  syncSingleAddress(addressId: string): Observable<HttpResponse<SingleSyncResponse>> {
    if (!addressId) {
      return of(new HttpResponse({
        body: {
          status: 'error',
          statusCode: 'INVALID_INPUT',
          message: 'ID de dirección requerido'
        },
        status: 400
      }));
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.businessPartnerAddressService.syncSingleAddressWithERP(addressId).pipe(
      tap(response => {
        // Actualizar la dirección específica en el estado
        this.refreshAddresses();
      }),
      catchError(error => {
        const errorMessage = error.error?.message || 'Error al sincronizar dirección';
        this.errorSubject.next(errorMessage);
        return of(new HttpResponse({
          body: {
            status: 'error',
            statusCode: 'SYNC_ERROR',
            message: errorMessage
          },
          status: 500
        }));
      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Refresca las direcciones con los filtros actuales
   */
  refreshAddresses(): void {
    const currentFilters = this.currentFiltersSubject.value;
    this.loadPendingAddresses(currentFilters).subscribe();
  }

  /**
   * Limpia el estado del servicio
   */
  clearState(): void {
    this.addressesSubject.next([]);
    this.loadingSubject.next(false);
    this.errorSubject.next(null);
    this.totalItemsSubject.next(0);
    this.currentFiltersSubject.next({});
    this.pageInfoSubject.next(null);
  }

  /**
   * Obtiene una dirección específica por ID
   */
  getAddressById(id: string): PendingAddress | undefined {
    return this.addressesSubject.value.find(address => address._id === id);
  }


}